import BareButton from 'primitives/Buttons/BareButton';
import HighlightsSDK from 'components/Highlights/HighlightsSdk';
import ListenInApp from 'components/ListenInApp';
import logger from 'modules/Logger';
import News from 'views/Podcast/PodcastProfile/LatestNews';
import PageBody, { ViewName } from 'views/PageBody';
import PodcastEpisodeCard from 'views/Podcast/PodcastEpisodeCard';
import PodcastEpisodeCards from 'views/Podcast/PodcastEpisodeCards';
import PodcastEpisodeHead from 'views/Podcast/PodcastProfile/PodcastEpisodeHead';
import PodcastEpisodesHead from 'views/Podcast/PodcastProfile/PodcastEpisodesHead';
import PodcastHero from 'views/Podcast/PodcastHero';
import PodcastHighlightsHead from 'views/Podcast/PodcastProfile/PodcastHighlightsHead';
import PodcastSideRail from 'views/Podcast/PodcastSideRail';
import PodcastTranscriptionContainer from './PodcastTranscription/PodcastTranscriptionContainer';
import PopularPodcasts from 'views/Podcast/PodcastProfile/PopularPodcasts';
import qs from 'qs';
import ReorderIconDown from 'styles/icons/ReorderDown';
import ReorderIconUp from 'styles/icons/ReorderUp';
import Section from 'primitives/Section';
import ShouldShow from 'components/ShouldShow';
import transport from 'api/transport';
import useMount from 'hooks/useMount';
import useTranslate from 'contexts/TranslateContext/useTranslate';
import { ASPECT_RATIOS } from 'utils/mediaServerImageMaker/opsString/constants';
import { calculateSlideDimensions } from 'utils/calculateSlideDimensions';
import { fit } from 'utils/mediaServerImageMaker/opsString';
import { getHighlightsConfig } from 'state/Config/selectors';
import { getHighlightsSDKEnabled } from 'state/Features/selectors';
import { getIsMobile } from 'state/Environment/selectors';
import { getPodcastUrl } from 'state/Podcast/helpers';
import { getPodcastVideoMapStatus } from 'state/Podcast/selectors';
import { Heading, SectionHeading } from 'components/SectionHeading';
import { LatestNews, NewsChild } from 'components/LatestNews';
import { Route } from 'react-router-dom';
import { setPodcastLastViewed } from 'state/Podcast/services';
import { useAdsContext } from 'ads/components/AdsProvider';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { AbTestGroups, User } from 'state/User/types';
import type { AxiosPromise } from 'axios';
import type { Episode, Host, PodcastArticle } from 'state/Podcast/types';

export type Props = {
  abTestGroups: AbTestGroups;
  adTargeting?: { providerId: number };
  ampUrl: string;
  articles: Array<PodcastArticle>;
  contentLink: string;
  description: string;
  episode: Episode | null;
  hosts: Array<Host>;
  imgUrl: string;
  pathname: string | null;
  profileId: number | null;
  routeParams?: {
    highlightsSlug?: string;
    [key: string]: string | undefined;
  };
  seedId: number;
  seedType: string;
  sessionId: string | null;
  showType: string | undefined;
  slug: string;
  title: string;
  user: User;
  newPodcastProfile: boolean;
};

const PODCAST_PROFILE_LIMIT = 5;

const HIGHLIGHTS_TEST_GROUP = 'webLegacyHighlights' as const;

const deriveSortAsc = (showType: Props['showType']) => showType === 'serial';

function PodcastProfile({
  abTestGroups,
  ampUrl,
  articles,
  contentLink,
  description,
  episode,
  hosts,
  imgUrl,
  pathname,
  profileId,
  routeParams,
  seedId,
  seedType,
  sessionId,
  showType,
  slug,
  title,
}: Props) {
  const translate = useTranslate();
  const isMobile = useSelector(getIsMobile);
  const highlightsConfig = useSelector(getHighlightsConfig);
  const [position, setPosition] = useState<number>();
  const clipSlugAndId = routeParams?.highlightsSlug;
  const [containerWidth, setContainerWidth] = useState(0);
  const { isAdsEnabled } = useAdsContext();
  const isHighlightsSDKEnabled = useSelector(getHighlightsSDKEnabled);
  const hasPodcastVideos = useSelector((state: any) =>
    getPodcastVideoMapStatus(state, { podcastId: seedId }),
  );
  const [showHighlights, setShowHighlights] = useState(false);

  // Check if highlights config has valid values (not just empty defaults)
  const hasValidHighlightsConfig = useMemo(
    () =>
      Boolean(
        highlightsConfig?.mobile?.apiKey && highlightsConfig?.desktop?.apiKey,
      ),
    [highlightsConfig?.desktop?.apiKey, highlightsConfig?.mobile?.apiKey],
  );

  useEffect(() => {
    setShowHighlights(() => {
      const testGroupMembership = abTestGroups?.[HIGHLIGHTS_TEST_GROUP] === 'B';

      return (
        isHighlightsSDKEnabled &&
        hasPodcastVideos &&
        hasValidHighlightsConfig &&
        testGroupMembership
      );
    });
  }, [
    abTestGroups,
    hasPodcastVideos,
    hasValidHighlightsConfig,
    isHighlightsSDKEnabled,
  ]);

  const [sortAsc, setsortAsc] = useState<boolean>(deriveSortAsc(showType));

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setsortAsc(deriveSortAsc(showType));
  }, [showType]);

  useEffect(() => {
    if (isMobile) {
      setContainerWidth(window.innerWidth);
    }
  }, [isMobile]);

  const { slideHeight: mobileSlideHeight } = calculateSlideDimensions({
    containerDimension: containerWidth,
    dimensionType: 'width',
    aspectRatio: 1,
    slidesPerView: 1.2,
  });

  useMount(() => {
    function updateLastViewed(): AxiosPromise<void | Error> {
      if (ampUrl && profileId && sessionId && seedId) {
        return transport(
          setPodcastLastViewed({
            ampUrl,
            profileId,
            sessionId,
            podcastId: seedId,
          }),
        );
      } else {
        return Promise.reject(
          new Error('required information missing for transport'),
        );
      }
    }
    updateLastViewed().catch(e => {
      const err = e instanceof Error ? e : new Error(e);
      logger.error(err.message, Object.values(err), {}, err);
    });
  });

  useMount(() => {
    if (__CLIENT__) {
      const queryString = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      });
      setPosition(Number(queryString.position));
    }
  });

  const headingButton = (
    <BareButton
      data-test="reorder-icon-button"
      onClick={() => setsortAsc(!sortAsc)}
    >
      <ShouldShow hiddenElement={<ReorderIconUp />} shouldShow={sortAsc}>
        <ReorderIconDown />
      </ShouldShow>
    </BareButton>
  );

  const markup = {
    hosts,
    description,
    image: imgUrl ?? undefined,
    name: title,
    url: pathname ?? undefined,
  };

  const highlightsSdkInitData = {
    mobile: {
      ...highlightsConfig.mobile,
      height: mobileSlideHeight,
    },
    desktop: highlightsConfig.desktop,
  };

  return (
    <>
      <ListenInApp seedId={seedId} seedType={seedType} />
      {clipSlugAndId ?
        <PodcastHighlightsHead />
      : <PodcastEpisodesHead />}

      {/* NESTED ROUTES SO THAT WE ONLY RERENDER EPISODES COMPONENT */}
      <Route
        exact
        path={[
          '/podcast/:slugifiedId',
          '/podcast/:slugifiedId/highlights/:highlightsSlug?',
        ]}
        render={() => (
          <>
            <PodcastHero />
            <PageBody
              dataTest={ViewName.PodcastProfile}
              hasHero
              more={<PodcastSideRail isAbTest />}
              richResultsMarkup={markup}
            >
              {/* IHRWEB-17219 - Moved title out of pagebody to avoid multiple H1 elements. Use Show title in Hero as H1 */}
              <Section
                data-test={`${ViewName.PodcastProfile}-section-top`}
                type="top"
              >
                <SectionHeading
                  data-test={`${ViewName.PodcastProfile}-section-heading`}
                >
                  <Heading as="h2">{translate('Episodes')}</Heading>
                  {headingButton}
                </SectionHeading>
              </Section>
              <PodcastEpisodeCards sortAsc={sortAsc} />
              {showHighlights ?
                <Section
                  data-test={`${ViewName.PodcastProfile}-section-highlights`}
                  type="top"
                >
                  <SectionHeading
                    data-test={`${ViewName.PodcastProfile}-section-heading`}
                  >
                    <Heading as="h2">{translate('Highlights')}</Heading>
                  </SectionHeading>
                  <HighlightsSDK
                    clipSlugAndId={clipSlugAndId}
                    highlightsSDKSourceType="legacy"
                    isAdsEnabled={isAdsEnabled}
                    // height={558}
                    podcastId={seedId}
                    podcastName={title}
                    {...(isMobile ?
                      highlightsSdkInitData.mobile
                    : highlightsSdkInitData.desktop)}
                  />
                </Section>
              : null}
              <News />
              <PopularPodcasts
                itemSelectedLocation="podcast_profile|popular_podcasts|tile"
                tilesInRow={PODCAST_PROFILE_LIMIT}
              />
            </PageBody>
          </>
        )}
      />

      {/* NESTED ROUTES SO THAT WE ONLY RERENDER EPISODE COMPONENT */}
      <Route
        exact
        path="/podcast/:slugifiedId/episode/:episodeSlug"
        render={() => {
          if (episode === null) return null;
          const { episodeTranscription, transcriptionAvailable } =
            episode as Episode;

          return (
            <>
              <PodcastEpisodeHead />
              <PageBody
                backLink={`/podcast/${slug}-${seedId}`}
                dataTest={ViewName.PodcastEpisode}
                more={<PodcastSideRail />}
                title={translate('All Episodes')}
              >
                <PodcastEpisodeCard
                  currentPodcast={{
                    title,
                    description,
                    hosts,
                    imgUrl,
                    pathname,
                  }}
                  onClick={() =>
                    ref?.current?.scrollIntoView({ behavior: 'smooth' })
                  }
                  position={position}
                />
                <PodcastTranscriptionContainer
                  episodeTranscription={episodeTranscription}
                  ref={ref}
                  transcriptionAvailable={transcriptionAvailable}
                />
                <LatestNews
                  heading={`${title} News`}
                  sectionUrl={`${getPodcastUrl(seedId, slug)}news/`}
                >
                  {articles.slice(0, 3).map(article => (
                    <NewsChild
                      href={`${contentLink}${article.slug}/`}
                      img={article.summary.image}
                      isSponsored={article.payload.is_sponsored}
                      key={article.slug.replace(' ', '').toLowerCase()}
                      ops={[fit(...ASPECT_RATIOS['16:9'])]}
                      title={article.summary.title}
                    />
                  ))}
                </LatestNews>
              </PageBody>
            </>
          );
        }}
      />
    </>
  );
}

export default PodcastProfile;
